import { type ComponentProps, type ReactNode, useMemo, useState } from 'react';
import { InfinityScrollSelect } from 'ui';
import { useDebounce } from 'common/hooks';
import { usePublicOrganizationsInfiniteQuery } from 'features/organizations';
import { useCreatePrimaryOrganizationOption } from './hooks';

export type PrimaryOrganizationOption = { label: ReactNode; optionLabel: ReactNode; value: string };

type P = Omit<
  ComponentProps<typeof InfinityScrollSelect<PrimaryOrganizationOption>>,
  'handleRequestMoreData' | 'hasMoreData'
>;

const PrimaryOrganizationSelect = (props: P) => {
  const [search, setSearch] = useState<string>();
  const { isDebouncing, value } = useDebounce(search);
  const createPrimaryOrganizationOption = useCreatePrimaryOrganizationOption();

  const { data, fetchNextPage, hasNextPage, isFetching } = usePublicOrganizationsInfiniteQuery({
    filter: { search: value },
    pagination: { pageSize: 10 },
  });

  const options = useMemo(() => {
    const organizations = data?.pages.flatMap((organizationsPaginated) => organizationsPaginated.data);

    return organizations?.map((organization) => createPrimaryOrganizationOption(organization));
  }, [createPrimaryOrganizationOption, data?.pages]);

  return (
    <InfinityScrollSelect<PrimaryOrganizationOption>
      fieldNames={{ label: 'optionLabel' }}
      handleRequestMoreData={fetchNextPage}
      hasMoreData={hasNextPage}
      loading={isDebouncing || isFetching}
      onSearch={setSearch}
      optionLabelProp="optionLabel"
      options={options}
      {...props}
    />
  );
};

export default PrimaryOrganizationSelect;
